import cx from 'classnames';

import { EventUserSchema, LiveEventAction } from 'types';
import { choosePaletteColor, getUserInitials } from 'utils';
import { isHandRaisedSelector, isInvitedToStageSelector } from 'store/selectors';
import { useLiveEventActions, useStore } from 'hooks';
import { useLiveEvent } from 'containers/LiveEvent';
import { Avatar } from 'components';
import { CheckedIcon, PlusIcon, StarIcon } from 'components/Icon';

import styles from './Guests.Viewer.module.scss';

export type ViewerGuestProps = {
  viewer: EventUserSchema;
  onInvite?(viewer: EventUserSchema): void;
};

const ViewerGuest = ({ viewer, onInvite }: ViewerGuestProps): JSX.Element => {
  const liveEvent = useLiveEvent();
  const isInvitedToStage = useStore(isInvitedToStageSelector(liveEvent.eventCode, viewer.username));
  const hasSentJoinRequest = useStore(isHandRaisedSelector(liveEvent.eventCode, viewer.username));
  const liveEventActions = useLiveEventActions(liveEvent.eventCode);

  const initials = getUserInitials(viewer.username);
  const fallbackColor = choosePaletteColor(initials);

  const handleOnInvite = () => {
    onInvite?.(viewer);
  };

  const renderInviteButton = () => {
    const hasPermission = liveEventActions.hasPermission(LiveEventAction.INVITE_VIEWER_TO_STAGE);
    const canBeInvited = !hasSentJoinRequest;

    if (!hasPermission || !canBeInvited) return null;

    return (
      <button
        type="button"
        className={cx(styles.action, styles.invite, {
          [styles.invited]: isInvitedToStage
        })}
        disabled={isInvitedToStage}
        onClick={handleOnInvite}
      >
        {isInvitedToStage ? (
          <>
            <CheckedIcon />
            <span>Invited</span>
          </>
        ) : (
          <>
            <PlusIcon />
            <span>Invite to stage</span>
          </>
        )}
      </button>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.avatar}>
        <Avatar className={styles.avatar} src={viewer.avatar} initials={initials} fallbackColor={fallbackColor} />
        {viewer.member && (
          <div className={styles.badge}>
            <StarIcon />
          </div>
        )}
      </div>

      <div className={styles.info}>
        <h4 className={styles.title}>{viewer.name}</h4>
      </div>
      <div className={styles.actions}>{renderInviteButton()}</div>
    </div>
  );
};

export default ViewerGuest;
